import React, { useEffect, useState, useRef } from 'react';
import { Markup } from 'interweave';
import { useAuth } from '../../../../hooks/useAuth';

import './style.css';
import { extractFirstFrame } from './utils';
import { isIOSSafariNotInstalled } from '../../../../utils/pwaDetect';

interface Tutorial {
  url: string;
  title: string;
  shortDescription: string;
  description: string;
  active: boolean;
}

interface OnboardingSectionProps {
  topContent?: React.ReactNode;
}

export default function OnboardingSection({
  topContent,
}: OnboardingSectionProps) {
  const { player } = useAuth();

  const [videosTutorials, setVideosTutorials] = useState<Tutorial[]>([]);
  const [videoPosters, setVideoPosters] = useState<Record<string, string>>({});
  const processedUrls = useRef<Set<string>>(new Set());
  const videoContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const videosTutorialsStatic = [
      {
        url: 'https://oqapi-production.s3.us-east-1.amazonaws.com/VideoTutorials/1768599145944-277fa6a0-21ed-4c37-901a-71e5461c3c4a',
        title: 'Como compartilhar uma publicação?',
        shortDescription: 'Veja como é fácil compartilhar uma publicação.',
        description:
          'Veja como é fácil compartilhar uma publicação. São apenas 3 passos simples:<br><br>1 - Escolha o que você quer compartilhar;<br>2 - Clique em uma das opções de compartilhamento;<br>3 - Publique ou envie o link para quantas pessoas ou grupos quiser;<br><br>Você pode ver mais detalhes clicando na publicação.',
        active: true,
      },
      {
        url: 'https://oqapi-production.s3.us-east-1.amazonaws.com/VideoTutorials/1768597566526-8c289575-8ecd-4392-a912-e9bb05562736',
        title: 'Como resgatar dinheiro?',
        shortDescription: 'Veja como é fácil resgatar dinheiro.',
        description:
          'Veja como é fácil resgatar dinheiro. São apenas alguns passos simples.<br><br>Siga o passo a passo e explicamos no video tutorial e resgate prêmios em dinheiro sem complicações.',
        active: true,
      },
    ];

    if (isIOSSafariNotInstalled()) {
      videosTutorialsStatic.unshift({
        url: 'https://oqapi-production.s3.us-east-1.amazonaws.com/VideoTutorials/1772804577653-90f567b8-a412-4cb8-b703-d5f3ad0010a8',
        title: 'Como instalar o app no iPhone?',
        shortDescription:
          'Veja como adicionar o app à tela inicial do seu iPhone.',
        description:
          'Veja como é fácil instalar nosso app no seu iPhone pelo Safari.<br><br>1 - Toque no botão de compartilhar (ícone de quadrado com seta para cima);<br>2 - Role as opções e toque em "Adicionar à Tela de Início";<br>3 - Confirme tocando em "Adicionar";<br><br>Pronto! O app estará disponível na sua tela inicial.',
        active: true,
      });
    }

    if (player && player.regional === 'sim') {
      videosTutorialsStatic.push(
        {
          url: 'https://oqapi-production.s3.us-east-1.amazonaws.com/VideoTutorials/1768862884506-2db14ec6-a4dc-40c8-bd9d-1f4e208542bf',
          title: 'Como captar leads?',
          shortDescription:
            'Veja como é fácil captar leads diretamente para você!',
          description: 'Veja como é fácil captar leads diretamente para você!',
          active: true,
        },
        {
          url: 'https://oqapi-production.s3.us-east-1.amazonaws.com/VideoTutorials/1768859689390-6c14a294-43cf-426f-9921-3cdb57033818',
          title: 'Como registrar seus próprios leads?',
          shortDescription: 'Veja como é fácil registrar seus leads!',
          description: 'Veja como é fácil registrar seus leads!',
          active: true,
        },
        {
          url: 'https://oqapi-production.s3.us-east-1.amazonaws.com/VideoTutorials/1768859800440-a0333c0c-507f-4f8b-9614-eaac79b4f083',
          title: 'Como resgatar vale compras?',
          shortDescription: 'Veja como resgatar seu vale compras!',
          description: 'Veja como resgatar seu vale compras!',
          active: true,
        },
        {
          url: 'https://oqapi-production.s3.us-east-1.amazonaws.com/VideoTutorials/1768863013017-e22fde63-2732-466f-8719-4247bb7cf720',
          title: 'Como responder o quiz?',
          shortDescription:
            'Aprenda a engajar com treinamento e a responder o Quiz!',
          description:
            'Aprenda a engajar com treinamento e a responder o Quiz!',
          active: true,
        },
        {
          url: 'https://oqapi-production.s3.us-east-1.amazonaws.com/VideoTutorials/1768862509982-e00e87f4-77e5-4ba9-8e4c-4fcc6ad56a52',
          title: 'Como indicar nossos produtos?',
          shortDescription:
            'Veja como é fácil indicar todos os produtos da plataforma!',
          description:
            'Veja como é fácil indicar todos os produtos da plataforma!',
          active: true,
        },
        {
          url: 'https://oqapi-production.s3.us-east-1.amazonaws.com/VideoTutorials/1768862726177-e6bef287-447e-4beb-862a-04fb1ba63085',
          title: 'Como validar presença pelo QR Code?',
          shortDescription:
            'Veja como é fácil validar sua presença gerando um QR Code!',
          description:
            'Veja como é fácil validar sua presença gerando um QR Code!',
          active: true,
        }
      );
      setVideosTutorials(videosTutorialsStatic);
    } else if (player && player.regional === 'nao') {
      videosTutorialsStatic.push({
        url: 'https://oqapi-production.s3.us-east-1.amazonaws.com/VideoTutorials/1768863291571-3b6ad3aa-efda-434d-9255-5c97845c3fe5',
        title: 'Como fazer uma indicação?',
        shortDescription: 'Veja como é fácil indicar uma pessoa!',
        description: 'Veja como é fácil indicar uma pessoa!',
        active: true,
      });
      setVideosTutorials(videosTutorialsStatic);
    } else {
      setVideosTutorials(videosTutorialsStatic);
    }
  }, []);

  useEffect(() => {
    if (videosTutorials.length === 0) return;

    const generatePosters = async () => {
      const posters: Record<string, string> = {};

      for (const video of videosTutorials) {
        if (video.url && !processedUrls.current.has(video.url)) {
          processedUrls.current.add(video.url);
          try {
            const poster = await extractFirstFrame(video.url);
            posters[video.url] = poster;
          } catch (error) {
            console.warn(`Failed to extract frame for ${video.title}:`, error);
          }
        }
      }

      if (Object.keys(posters).length > 0) {
        setVideoPosters((prev) => ({ ...prev, ...posters }));
      }
    };

    generatePosters();
  }, [videosTutorials]);

  const [initialIndexTutorial, setInitialIndexTutorial] = useState(0);

  function activeTutorial(e) {
    const cardElement = document.querySelectorAll('.oq-item-tutorial');
    cardElement.forEach((element) => {
      element.classList.remove('active');
    });
    e.currentTarget.classList.add('active');
    setInitialIndexTutorial(e.currentTarget.dataset.index);

    videoContainerRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  return (
    <>
      {videosTutorials.length > 0 && (
        <div className="row">
          <div className="col-sm-12 col-md-8">
            {topContent && (
              <div className="ob-how-to-card-group mb-3">{topContent}</div>
            )}
            <div className="ob-how-to-card-group">
              <div
                className="ob-how-to-responsive-video"
                ref={videoContainerRef}
              >
                <video
                  src={videosTutorials[initialIndexTutorial].url}
                  poster={
                    videoPosters[videosTutorials[initialIndexTutorial].url] ||
                    ''
                  }
                  controls
                  preload="auto"
                ></video>
              </div>
              <div className="ob-how-to-card video-header">
                <h2>{videosTutorials[initialIndexTutorial].title}</h2>
                <p>
                  <Markup
                    content={videosTutorials[initialIndexTutorial].description}
                  />
                </p>
              </div>
            </div>
          </div>
          <div className="col-sm-12 col-md-4">
            <div className="ob-how-to-card-group">
              <div className="ob-how-to-card header">
                <h2>Videos tutoriais</h2>
                <p className="">
                  A lista abaixo ajudará você a utilizar nossa plataforma de
                  maneira simples. São videos curtos e objetivos. Acesse quando
                  quiser.
                </p>
              </div>
              {videosTutorials.map((video, index) => {
                if (video.active) {
                  return (
                    <div
                      key={video.url}
                      className={
                        index === 0
                          ? 'ob-how-to-card oq-item-tutorial active'
                          : 'ob-how-to-card oq-item-tutorial'
                      }
                      data-index={index}
                      onClick={(e) => {
                        activeTutorial(e);
                      }}
                    >
                      <span className="ob-how-to-card-title">
                        <i className="material-icons-outlined">play_arrow</i>{' '}
                        {video.title}
                      </span>
                      <p className="ob-how-to-content-item-description">
                        {video.shortDescription}
                      </p>
                    </div>
                  );
                }
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
